import { createRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import NumberFormat from "react-number-format";
import ChangeHighlight from "react-change-highlight";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import { secondaryColor } from "utils/constants";
import styles from "assets/shared-stylesheets/sparket-list-item.module.css";
import highlightStyles from "components/pool-details/bet-modal/bet-modal-content/styles.module.css";
import BackCardShell from "./BackCardShell";
import { BackCardProps } from "./types";
import { CardHeader, WinConditionRow, Disclaimer } from "./SharedParts";
import popStyles from "./pop.module.css";

/**
 * Variant C: Combines everything Evan suggested.
 *  - Values use variable black/white bold text.
 *  - Balance/Picks centered with bigger picks number (basic) and smaller fit
 *    for longer currency strings (pro).
 *  - Pop animation on entry — values scale up briefly when the card mounts
 *    (simulating the flip). ChangeHighlight stays on Exp. Return / Odds.
 */
const VariantCombined = (props: BackCardProps) => {
  const {
    eventName,
    poolName,
    winCondition,
    contestantName,
    isBasic,
    picksRemaining,
    expReturn,
    balanceLabel,
    balanceValue,
    oddsLabel,
    betAmount,
    onBetAmountChange,
    onConfirm,
    onCancel,
    loading,
    disabled,
  } = props;

  const valueStyle = {
    color: "var(--text-color)",
    fontWeight: 800 as const,
  };

  return (
    <BackCardShell>
      <CardHeader
        eventName={eventName}
        poolName={poolName}
        onCancel={onCancel}
      />
      <WinConditionRow winCondition={winCondition} />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ overflow: "hidden", flex: 1, minWidth: 0 }}>
          <BlockyHeavyText
            className={popStyles.pop}
            style={{
              fontSize: 16,
              ...valueStyle,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {contestantName}
          </BlockyHeavyText>
        </div>
        <div style={{ textAlign: "center", minWidth: 110 }}>
          <BlockyHeavyText
            className={styles.poolCloseDatePrefix}
            style={{ textAlign: "center" }}
          >
            {isBasic ? "Picks Remaining" : balanceLabel || "Balance"}
          </BlockyHeavyText>
          <BlockyBoldText
            className={popStyles.pop}
            style={{
              fontSize: isBasic ? 28 : 18,
              ...valueStyle,
              lineHeight: 1.1,
              textAlign: "center",
            }}
          >
            {isBasic ? picksRemaining ?? 0 : balanceValue}
          </BlockyBoldText>
        </div>
      </div>

      <div>
        {isBasic ? (
          <div style={{ padding: 3 }}>
            <Button
              style={{
                width: "100%",
                height: 41,
                backgroundColor: secondaryColor,
                color: "black",
              }}
              disabled={loading || disabled}
              onClick={onConfirm}
            >
              {loading && (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ marginRight: 10 }}
                />
              )}
              Confirm
            </Button>
            <div
              style={{
                marginTop: 7,
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <BlockyBoldText>Exp. Return: &nbsp;</BlockyBoldText>
              <BlockyBoldText>
                <ChangeHighlight
                  hideAfter={1000}
                  highlightClassName={highlightStyles.highlight}
                >
                  <span
                    ref={createRef()}
                    className={popStyles.pop}
                    style={{ ...valueStyle, fontSize: 16 }}
                  >
                    {expReturn}
                  </span>
                </ChangeHighlight>
              </BlockyBoldText>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", padding: 3 }}>
            <div style={{ flex: 0.5 }}>
              <NumberFormat
                value={betAmount}
                onValueChange={(v) => onBetAmountChange?.(v.formattedValue)}
                prefix="§"
                decimalScale={2}
                thousandSeparator
                inputMode="decimal"
                fixedDecimalScale
                allowNegative={false}
                placeholder="Enter Amount"
                style={{
                  borderRadius: 5,
                  height: 40,
                  color: "black",
                  width: "95%",
                  fontSize: 16,
                }}
              />
              <div style={{ display: "flex" }}>
                <BlockyBoldText
                  style={{
                    marginTop: 7,
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  Odds: &nbsp;
                  <ChangeHighlight
                    hideAfter={1000}
                    highlightClassName={highlightStyles.highlight}
                  >
                    <div
                      ref={createRef()}
                      className={popStyles.pop}
                      style={{ ...valueStyle, fontSize: 16 }}
                    >
                      {oddsLabel}
                    </div>
                  </ChangeHighlight>
                </BlockyBoldText>
              </div>
            </div>
            <div style={{ width: "100%", flex: 0.5 }}>
              <Button
                style={{
                  width: "100%",
                  height: 41,
                  backgroundColor: secondaryColor,
                  color: "black",
                }}
                disabled={loading || disabled}
                onClick={onConfirm}
              >
                {loading && (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginRight: 10 }}
                  />
                )}
                Confirm
              </Button>
              <div style={{ marginTop: 7 }}>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <BlockyBoldText>Exp. Return: &nbsp;</BlockyBoldText>
                  <BlockyBoldText>
                    <ChangeHighlight
                      hideAfter={1000}
                      highlightClassName={highlightStyles.highlight}
                    >
                      <span
                        ref={createRef()}
                        className={popStyles.pop}
                        style={{ ...valueStyle, fontSize: 16 }}
                      >
                        {expReturn}
                      </span>
                    </ChangeHighlight>
                  </BlockyBoldText>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Disclaimer />
    </BackCardShell>
  );
};

export default VariantCombined;
